import { useMemo } from 'react';
import { useTheme } from 'next-themes';
import { useQuery } from '@zg-rentals/react-query5';
import {
  buildStaticMapProxyUrl,
  buildStaticMapUrl,
  buildStaticMapUrlForBounds,
  type StaticMapBounds,
} from '@/src/lib/utils/staticMap';
import { getStaticMapBoundaryStyle } from '@/src/lib/map/areaBoundaryStyle';
import type { StaticMapBoundaryPaths } from '@/src/lib/map/staticMapBoundaryPaths';

type UseStaticMapUrlOptions = {
  bounds: StaticMapBounds | undefined;
  width?: number;
  height?: number;
  scale?: number;
  zoom?: number;
  /** Area outline and outside shade; only drawn when the camera fits `bounds` (no `zoom`). */
  boundary?: StaticMapBoundaryPaths | null;
};

const isProduction = process.env.NODE_ENV === 'production' && process.env.ZG_ENV !== 'development';

// A 2x image on a 1x display costs ~3x the bytes for pixels the screen cannot
// show (measured 37.8KB vs 12.4KB at 246x182), so follow the device.
const resolveStaticMapScale = (): 1 | 2 => {
  if (typeof window === 'undefined') return 2;
  return window.devicePixelRatio > 1 ? 2 : 1;
};

/**
 * Returns a Google Static Maps image URL for the given bounding box.
 *
 * Production / preview: returns a same-origin image proxy URL. The route signs
 * and fetches the Google Static Maps image server-side so preview/local referrer
 * restrictions do not break browser image requests.
 *
 * Dev: appends `NEXT_PUBLIC_GOOGLE_MAPS_KEY` directly to avoid routing every
 * thumbnail through Next while iterating locally.
 */
export const useStaticMapUrl = ({
  bounds,
  width = 246,
  height = 182,
  scale,
  zoom,
  boundary,
}: UseStaticMapUrlOptions) => {
  const staticMapScale = scale ?? resolveStaticMapScale();
  // next-themes resolves the theme in its first client render, so the first request already has
  // the right style. Without a provider (or on the server) it is undefined: light.
  const { resolvedTheme } = useTheme();
  const scheme = resolvedTheme === 'dark' ? 'dark' : 'light';
  // Finite so a single legitimate 0 bound stays enabled; the all-zero box is
  // the missing-coordinates sentinel.
  const enabled = Boolean(
    bounds &&
    [bounds.minLat, bounds.maxLat, bounds.minLon, bounds.maxLon].every(Number.isFinite) &&
    !(bounds.minLat === 0 && bounds.maxLat === 0 && bounds.minLon === 0 && bounds.maxLon === 0),
  );

  const { minLat, maxLat, minLon, maxLon } = bounds ?? {};
  // A boundary can add a few KB of path to the URL, so build it only when an input changes.
  const unsignedUrl = useMemo(() => {
    if (!enabled || minLat === undefined || maxLat === undefined || minLon === undefined || maxLon === undefined) {
      return '';
    }
    const box = { minLat, maxLat, minLon, maxLon };
    // scheme stays a dependency: the boundary style resolves tokens for the current color mode.
    const boundaryStyle = boundary ? getStaticMapBoundaryStyle() : null;
    return zoom === undefined
      ? buildStaticMapUrlForBounds(box, {
          width,
          height,
          scale: staticMapScale,
          format: 'jpg',
          colorScheme: scheme,
          boundary,
          boundaryStyle,
        })
      : buildStaticMapUrl({
          lat: (minLat + maxLat) / 2,
          lon: (minLon + maxLon) / 2,
          width,
          height,
          scale: staticMapScale,
          zoom,
          format: 'jpg',
          colorScheme: scheme,
        });
  }, [enabled, minLat, maxLat, minLon, maxLon, zoom, width, height, staticMapScale, scheme, boundary]);

  return useQuery({
    queryKey: ['staticMap', unsignedUrl],
    queryFn: () => {
      if (!isProduction && process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY) {
        return Promise.resolve(`${unsignedUrl}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY}`);
      }
      return Promise.resolve(buildStaticMapProxyUrl(unsignedUrl));
    },
    enabled,
    staleTime: Infinity,
    // Finite SSR cleanup timers retain the request's entire QueryClient until they fire.
    gcTime: typeof window === 'undefined' ? Infinity : 10 * 60 * 1000,
  });
};
